import { ArrayUtils, StringUtils, Logger, engineComponent, Utils } from "neo-common";
import { ParticleStyle } from "../Styles/ParticleStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { ComponentTypes } from "../../ComponentTypes";
import { AnimationFrameBaseElement } from "../../../AnimationFrame/AnimationFrame";
import { StageController } from "../../StageController";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { Tweens, ITweenable, TweensAttributes } from "../../Effects/Tweens";
import { Attributes } from "../../../Utils/Attributes";
import { BaseElement } from "./BaseElement";
import { CustomUtils } from "../../../Utils/CustomUtils";

var protonProto: any = window["Proton"];

@engineComponent(ComponentTypes.DisplayObject)
export class ProtonElement extends AnimationFrameBaseElement {
    static Pool: Object = {};

    private _animationName: string = "";

    // style is mandatory
    private _style: ParticleStyle;
    private _styleName: string;
    private _proton: Proton.Proton;
    private _renderer: Proton.Renderer;
    private _emitters: Array<Proton.Emitter>;

    private _tint: number;
    private _blendMode: number;
    private _emitting: boolean;
    private _stopping: boolean;
    private _textures: Array<PIXI.Texture>;
    private _animationSpeed: number;
    private _animationLoop: boolean;
    private _animationRandomStart: boolean = false;

    private delayStartTimer: FrameTimerEventEmitter;
    private delayStoppingTimer: FrameTimerEventEmitter;
    private delayStopTimer: FrameTimerEventEmitter;

    private _emitterBaseElement: EmitterBaseElement;

    private _randomSeedIndex = 0;
    private _randomSeed: number[] = [];
    private _randomSeedLength: number = 10000;

    constructor() {
        super();

        this._emitting = false;
        this._stopping = true;
        //this._stopped = true;
        this._animationSpeed = 1;
        this._animationLoop = true;
        this._emitters = [];
        this._proton = new protonProto() as Proton.Proton;
        this._tint = 0xffffff;

        this.onProtonUpdate = this.onProtonUpdate.bind(this);
        this.onParticleCreated = this.onParticleCreated.bind(this);
        this.onParticleUpdate = this.onParticleUpdate.bind(this);
        this.onParticleDead = this.onParticleDead.bind(this);
        this.transformSprite = this.transformSprite.bind(this);
        this.alphaDead = this.alphaDead.bind(this);
        this.scaleDead = this.scaleDead.bind(this);

        this.delayStartTimer = CreateFrameTimer("TimelineElement");
        this.delayStoppingTimer = CreateFrameTimer("TimelineElement");
        this.delayStopTimer = CreateFrameTimer("TimelineElement");
    }

    protected frame_onFrame(): void {
        if (this.super2.visible === true) {
            this._proton.update();
        }
    }

    protected get frame_name(): string {
        return this._styleName;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        this.super2._create(node);

        Tweens.removeTweens(this.super2, "alpha");
        Tweens.removeTweens(this.super2, "position");
        Tweens.removeTweens(this.super2, "rotation");
        Tweens.removeTweens(this.super2, "scale");

        /*
         * Tint of animation. Hex color which colorize movie clip.
         * @property Tint
         * @type string
         * @default null
         */
        if (node.hasAttribute("Tint")) {
            this._tint = parseInt(node.getAttribute(ProtonElementAttributes.TINT), 16);
        }

        /*
         * Blend mode for animation.
         * Bleding modes:
         *   0 = NORMAL;
         *   1 = ADD;
         *   2 = MULTIPLY;
         *   3 = SCREEN;
         *   4 = OVERLAY;
         *   5 = DARKEN;
         *   6 = LIGHTEN;
         *   7 = COLOR_DODGE;
         *   8 = COLOR_BURN;
         *   9 = HARD_LIGHT;
         *   10 = SOFT_LIGHT;
         *   11 = DIFFERENCE;
         *   12 = EXCLUSION;
         *   13 = HUE;
         *   14 = SATURATION;
         *   15 = COLOR;
         *   16 = LUMINOSITY;
         * @property BlendMode
         * @type number
         * @default null
         */
        if (node.hasAttribute("BlendMode")) {
            this._blendMode = CustomUtils.getBlendMode(parseInt(node.getAttribute("BlendMode")));
        }

        /*
         * Style define animation. You can use one style on more than one place.
         * Style for ParticleElement has to be instance of ParticleStyle.
         * @property Style
         * @type string
         * @default null
         */
        if (node.hasAttribute("Style") || node.hasAttribute("Style2")) {
            if (node.hasAttribute("Style")) {
                this._style = <ParticleStyle>StyleManager.Styles[node.getAttribute("Style")];
                this._styleName = this._style.node.nodeName;
            } else {
                this._style = <ParticleStyle>StyleManager.Styles[node.getAttribute("Style2")];
                this._styleName = this._style.node.nodeName;
            }

            if (this._style != null) {
                var next: Element = this._style.node.firstElementChild;
                while (next != null) {
                    if (next.nodeName === "Emitter") {
                        var emitter: Proton.Emitter = new Proton.Emitter();

                        this._emitters.push(emitter);

                        var emitterNext: Element = next.firstElementChild;
                        while (emitterNext != null) {
                            switch (emitterNext.nodeName) {
                                case "Damping":
                                    let damping: number = parseFloat(emitterNext.getAttribute("Value"));
                                    if (!isNaN(damping)) {
                                        emitter.damping = damping;
                                    } else {
                                        if (DEBUG) {
                                            this.log("WARNING Damping does not have numeric Value");
                                        }
                                    }
                                    break;
                                case "Rate":
                                    if (emitterNext.hasAttribute("Rate")) {
                                        let rate: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("Rate"),
                                        );
                                        if (rate.length === 2) {
                                            emitter.rate = new Proton.Rate(rate[0], rate[1]);
                                        } else {
                                            if (DEBUG) {
                                                this.log(
                                                    "WARNING Rate has different number of parameters. It has to have two parameters.",
                                                );
                                            }
                                        }
                                    } else if (
                                        emitterNext.hasAttribute("RateStart") &&
                                        emitterNext.hasAttribute("RateEnd")
                                    ) {
                                        /**
                                         * @deprecated Misleading naming of RateStart. Instead this use NumPan.
                                         * @type {Array<number>}
                                         */
                                        let rateStart: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("RateStart"),
                                        );
                                        /**
                                         * @deprecated Misleading naming of RateEnd. Instead this use TimePan.
                                         * @type {Array<number>}
                                         */
                                        let rateEnd: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("RateEnd"),
                                        );
                                        emitter.rate = new Proton.Rate(
                                            new Proton.Span(rateStart[0], rateStart[1]),
                                            new Proton.Span(rateEnd[0], rateEnd[1]),
                                        );
                                    } else if (
                                        emitterNext.hasAttribute("NumPan") &&
                                        emitterNext.hasAttribute("TimePan")
                                    ) {
                                        let numPan: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("NumPan"),
                                        );
                                        let timePan: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("TimePan"),
                                        );
                                        emitter.rate = new Proton.Rate(
                                            new Proton.Span(numPan[0], numPan[1]),
                                            new Proton.Span(timePan[0], timePan[1]),
                                        );
                                    } else {
                                        if (DEBUG) {
                                            this.log("WARNING Rate has wrong attributes");
                                        }
                                    }
                                    break;
                                case "Initializers":
                                    this.createInitializers(emitterNext, this._emitters[this._emitters.length - 1]);
                                    break;
                                case "Behaviours":
                                    this.createBehaviours(emitterNext, this._emitters[this._emitters.length - 1]);
                                    break;
                                default:
                                    if (DEBUG) {
                                        this.log("WARNING unknown type for emitter");
                                    }
                            }
                            emitterNext = emitterNext.nextElementSibling;
                        }

                        if (next.hasAttribute("Emit")) {
                            emitter.emit(next.getAttribute("Emit"));
                        } else {
                            emitter.emit();
                        }

                        if (next.hasAttribute("X")) {
                            emitter["p"].x = parseInt(next.getAttribute("X"));
                        }

                        if (next.hasAttribute("Y")) {
                            emitter["p"].y = parseInt(next.getAttribute("Y"));
                        }
                    }
                    next = next.nextElementSibling;
                }

                if (this._emitters != null) {
                    for (var i = 0; i < this._emitters.length; i++) {
                        this._proton.addEmitter(this._emitters[i]);
                    }

                    this._renderer = new Proton.Renderer("other", this._proton);
                    this._renderer.onProtonUpdate = this.onProtonUpdate;
                    this._renderer.onParticleCreated = this.onParticleCreated;
                    this._renderer.onParticleUpdate = this.onParticleUpdate;
                    this._renderer.onParticleDead = this.onParticleDead;
                    this._emitting = false;
                    this.frame_stop();

                    if (this._emitterBaseElement == null) {
                        this._emitterBaseElement = new EmitterBaseElement();

                        if (this._emitterBaseElement != null) {
                            this._emitterBaseElement._protonEmitters = this._emitters;
                            this.updateByNode(node);
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no emitters");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute("Style"));
                }
            }
        }
    }

    public runAction(
        startCanRange?: string,
        durationCanRange?: string,
        emit?: string,
        stoppingCanRange?: string,
        stopCanRange?: string,
    ): void {
        if (startCanRange) {
            this.startByParam(startCanRange, durationCanRange, emit);
        }

        if (stoppingCanRange) {
            this.stoppingByParam(stoppingCanRange);
        }

        if (stopCanRange) {
            this.stopByParam(stopCanRange);
        }
    }

    private startByParam(startCanRange: string, durationCanRange: string, emit: string): void {
        if (startCanRange) {
            let valueArray: Array<number> = startCanRange.split(";").map((value) => parseInt(value));
            let value =
                valueArray.length > 1
                    ? valueArray[0] + (valueArray[0] + Math.round(Math.random() * (valueArray[1] - valueArray[0])))
                    : valueArray[0];

            if (!isNaN(value)) {
                this.delayStartTimer.stop();
                this.delayStartTimer.on(FrameTimerEvents.Complete, this.start.bind(this, emit));
                this.delayStartTimer.start(value);
                if (durationCanRange) {
                    let durValueArray: Array<number> = durationCanRange.split(";").map((value) => parseInt(value));
                    let durValue =
                        durValueArray.length > 1
                            ? durValueArray[0] +
                              (durValueArray[0] + Math.round(Math.random() * (durValueArray[1] - durValueArray[0])))
                            : durValueArray[0];
                    if (!isNaN(durValue)) {
                        this.delayStoppingTimer.stop();
                        this.delayStoppingTimer.on(FrameTimerEvents.Complete, this.stopping.bind(this));
                        this.delayStoppingTimer.start(value + durValue);
                    }
                }
            } else if (StringUtils.parseBoolean(startCanRange)) {
                this.start(emit);
                if (durationCanRange) {
                    let durValueArray: Array<number> = durationCanRange.split(";").map((value) => parseInt(value));
                    let durValue =
                        durValueArray.length > 1
                            ? durValueArray[0] +
                              (durValueArray[0] + Math.round(Math.random() * (durValueArray[1] - durValueArray[0])))
                            : durValueArray[0];
                    if (!isNaN(durValue)) {
                        let emitValue = parseInt(emit) || 0;
                        this.delayStoppingTimer.stop();
                        this.delayStoppingTimer.on(FrameTimerEvents.Complete, this.stopping.bind(this));
                        this.delayStoppingTimer.start(emitValue + durValue);
                    }
                }
            }
        }
    }

    private stoppingByParam(stoppingCanRange: string): void {
        if (stoppingCanRange) {
            let valueArray: Array<number> = stoppingCanRange.split(";").map((value) => parseInt(value));
            let value =
                valueArray.length > 1
                    ? valueArray[0] + (valueArray[0] + Math.round(Math.random() * (valueArray[1] - valueArray[0])))
                    : valueArray[0];
            if (!isNaN(value)) {
                this.delayStoppingTimer.stop();
                this.delayStoppingTimer.on(FrameTimerEvents.Complete, this.stopping.bind(this));
                this.delayStoppingTimer.start(value);
            } else if (StringUtils.parseBoolean(stoppingCanRange)) {
                this.stopping();
            }
        }
    }

    private stopByParam(stopCanRange: string): void {
        if (stopCanRange) {
            let valueArray: Array<number> = stopCanRange.split(";").map((value) => parseInt(value));
            let value =
                valueArray.length > 1
                    ? valueArray[0] + (valueArray[0] + Math.round(Math.random() * (valueArray[1] - valueArray[0])))
                    : valueArray[0];
            if (!isNaN(value)) {
                this.delayStopTimer.stop();
                this.delayStopTimer.on(FrameTimerEvents.Complete, this.stop.bind(this));
                this.delayStopTimer.start(value);
            } else if (StringUtils.parseBoolean(stopCanRange)) {
                this.stop();
            }
        }
    }

    public updateByNode(node: Element): void {
        if (node.hasAttribute(ProtonElementAttributes.START)) {
            this.startByParam(
                node.getAttribute(ProtonElementAttributes.START),
                node.getAttribute(ProtonElementAttributes.DURATION),
                node.getAttribute(ProtonElementAttributes.EMIT),
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.STOPPING)) {
            this.stoppingByParam(node.getAttribute(ProtonElementAttributes.STOPPING));
        }

        if (node.hasAttribute(ProtonElementAttributes.STOP)) {
            this.stopByParam(node.getAttribute(ProtonElementAttributes.STOP));
        }

        if (node.hasAttribute(ProtonElementAttributes.X)) {
            this.super2.position.x = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.X,
                this.super2.position.x,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.Y)) {
            this.super2.position.y = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.Y,
                this.super2.position.y,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.PIVOT_X)) {
            this.super2.pivot.x = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.PIVOT_X,
                this.super2.pivot.x,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.PIVOT_Y)) {
            this.super2.pivot.y = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.PIVOT_Y,
                this.super2.pivot.y,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.EMITTER_X)) {
            this._emitterBaseElement.x = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.EMITTER_X,
                this._emitterBaseElement.x,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.EMITTER_Y)) {
            this._emitterBaseElement.y = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.EMITTER_Y,
                this._emitterBaseElement.y,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.VISIBLE)) {
            this.super2.visible = Attributes.assignBooleanAttribute(
                node,
                ProtonElementAttributes.VISIBLE,
                this.super2.visible,
            );
        }

        if (node.hasAttribute(ProtonElementAttributes.RESET_BEHAVIORS)) {
            this._emitters.forEach((emitter) => {
                emitter.behaviours.forEach((behaviour) => {
                    behaviour.energy = 1;
                });
            });
            node.getAttribute(ProtonElementAttributes.RESET_BEHAVIORS);
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);

        this.runPositionTweenEmitter(node);
        this.runRotationTweenEmitter(node);
    }

    set tint(value: number) {
        this._tint = value;
    }

    get tint(): number {
        return this._tint;
    }

    start(emit: string = ""): void {
        if (this._stopping) {
            this._stopping = false;
        }
        if (this.isFrameStopped) {
            if (this._renderer != null) {
                this._renderer.stop();
            }

            this.removeChilds();

            if (this._emitters != null && this._emitters.length > 0) {
                if (emit != "") {
                    for (var i: number = 0; i < this._emitters.length; i++) {
                        this._emitters[i].emit(emit);
                    }
                }
            }
            if (this._renderer != null) {
                this._renderer.start();
                this._stopping = false;
                this._emitting = true;
                this.frame_start();
            }
        }
    }

    private stopping(): void {
        this._emitting = false;
        this._stopping = true;
    }

    private onProtonUpdate(): void {
        var emmiter0 = this._emitters[0];

        if (this._emitterBaseElement) {
            this._emitterBaseElement.updateEmitterParameters();
        }

        if (emmiter0["emitTotalTimes"] === "none" && emmiter0["particles"].length === 0) {
            this._emitting = false;
            this.frame_stop();
            this._renderer.stop();

            this.removeChilds();
        }

        if (this._stopping && !this._emitting && this.super2.children.length <= 0) {
            if (this._emitterBaseElement) {
                this._emitterBaseElement.stopUpdateEmitterParameters();
            }

            this.frame_stop();
            this._renderer.stop();
        }
    }

    private getRandomStartingFrame(): number {
        this._randomSeedIndex++;
        if (this._randomSeedIndex >= this._randomSeed.length) {
            this._randomSeedIndex = 0;
        }
        return this._randomSeed[this._randomSeedIndex];
    }

    private onParticleCreated(particle: any): void {
        if (this._stopping === false) {
            particle.sprite = null;
            if (particle.sprite != null) {
                if (this._textures != null && this._textures.length > 0) {
                    ProtonElement.Pool[this._animationName].push(particle.sprite);
                    this.super2.removeChild(particle.sprite);
                } else {
                    ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                    this.super2.removeChild(particle.sprite);
                }
            }
            let particleSprite: any;
            if (this._textures != null && this._textures.length > 0) {
                if (ProtonElement.Pool[this._animationName] == null) {
                    ProtonElement.Pool[this._animationName] = [];
                }

                if (ProtonElement.Pool[this._animationName].length === 0) {
                    particleSprite = new PIXI.extras.AnimatedSprite(this._textures);
                    particleSprite.name = this._animationName;
                } else {
                    particleSprite = ProtonElement.Pool[this._animationName].shift();
                }

                particleSprite.loop = this._animationLoop;
                particleSprite.animationSpeed = this._animationSpeed;
                if (this._animationRandomStart) {
                    particleSprite.gotoAndPlay(this.getRandomStartingFrame());
                } else {
                    particleSprite.gotoAndPlay(0);
                }
            } else {
                if (ProtonElement.Pool[particle.target.textureCacheIds[0]] == null) {
                    ProtonElement.Pool[particle.target.textureCacheIds[0]] = [];
                }

                if (ProtonElement.Pool[particle.target.textureCacheIds[0]].length === 0) {
                    particleSprite = new PIXI.Sprite(particle.target);
                    particleSprite.name = particle.target.textureCacheIds[0];
                } else {
                    particleSprite = ProtonElement.Pool[particle.target.textureCacheIds[0]].shift();
                }
            }
            if (this._tint != null) {
                particleSprite.tint = this._tint;
            }

            if (this._blendMode != null) {
                particleSprite.blendMode = this._blendMode;
            }

            particleSprite.visible = true;
            particle.sprite = particleSprite;
            this.super2.addChild(particleSprite);
        }
    }

    private onParticleUpdate(particle: any): void {
        if (particle != null && particle.sprite != null) {
            this.transformSprite(particle.sprite, particle);
        }
    }

    private onParticleDead(particle: any) {
        if (particle.sprite != null) {
            particle.sprite.visible = false;
            if (particle.sprite instanceof PIXI.extras.AnimatedSprite) {
                particle.sprite.stop();
            }
            this.super2.removeChild(particle.sprite);
        }

        particle.sprite = null;
        particle.dead = true;
        particle = null;
    }

    /**
     * Transform sprite in particle with proton.
     * @method transformSprite
     * @param particleSprite any
     * @param particle any
     * @return void
     */
    private transformSprite(particleSprite: any, particle: any) {
        if (
            particleSprite.transform != null &&
            !Utils.isNullOrUndefined(particleSprite) &&
            !Utils.isNullOrUndefined(particle)
        ) {
            particleSprite.visible = true;
            particleSprite.position.x = particle.p.x;
            particleSprite.position.y = particle.p.y;
            particleSprite.scale.x = particle.scale;
            particleSprite.scale.y = particle.scale;
            particleSprite.anchor.x = 0.5;
            particleSprite.anchor.y = 0.5;
            particleSprite.alpha = particle.alpha;
            particleSprite.rotation = (particle.rotation * Math.PI) / 180;
        } else {
            particle.dead = true;
        }
    }

    /**
     * Stop emitting particles.
     * @method stop
     * @return void
     */
    stop(): void {
        this._emitting = false;
        this._stopping = true;
        this.frame_stop();

        for (var i: number = 0; i < this._emitters.length; i++) {
            this._emitters[i].resetParticles();
        }

        if (this._renderer != null) {
            this._renderer.stop();
        }

        if (this._emitterBaseElement) {
            this._emitterBaseElement.stopUpdateEmitterParameters();
        }

        this.removeChilds();
    }

    removeChilds(): void {
        if (this.super2.children != null) {
            let i: number = 0;
            while (this.super2.children.length !== 0) {
                if (this.super2.children[0] != null) {
                    if (this._textures != null && this._textures.length > 0) {
                        (this.super2.children[0] as PIXI.extras.AnimatedSprite).stop();
                        ProtonElement.Pool[this._animationName].push(this.super2.children[0]);
                    } else {
                        ProtonElement.Pool[this.super2.children[0].name].push(this.super2.children[0]);
                    }
                    this.super2.removeChild(this.super2.children[0]);
                }
            }
        }
    }

    internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        this._emitting = false;
        this._stopping = true;
        this.frame_stop();
        this._textures = null;

        if (this._emitterBaseElement) {
            this._emitterBaseElement.stopUpdateEmitterParameters();
        }

        if (this._emitters != null && this._emitters.length > 0) {
            for (var i = 0; i < this._emitters.length; i++) {
                if (this._emitters[i] != null) {
                    this._proton.removeEmitter(this._emitters[i]);
                    this._emitters[i].destroy();
                    this._emitters[i] = null;
                }
            }
        }

        // removeEmitter
        // particleSleep
        // destroy

        if (this._renderer != null) {
            this._renderer.stop();
            this._renderer = null;
        }

        this._proton.destroy();
        this._proton = null;

        this.removeChilds();
    }

    /**
     * Create initializers from xml for emitter instance.
     * Initializers create start values and behaviour for particle instances.
     * @method createInitializers
     * @param node is Element from xml
     * @param emitter Proton.Emitter
     * @return void
     */
    private createInitializers(node: Element, emitter: Proton.Emitter): void {
        var next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case "ImageTarget":
                    var imageTarget: Proton.ImageTarget = this.addImageTarget(next);
                    if (imageTarget != null) {
                        emitter.addInitialize(imageTarget);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING ImageTarget is null");
                        }
                    }
                    break;
                case "AnimationTarget":
                    this.addAnimationTarget(next);
                    if (next.getAttribute("RandomStart") == "true") {
                        this._animationRandomStart = true;
                    }
                    break;
                case "Radius":
                    var radius: Proton.Radius = this.addRadius(next);
                    if (radius != null) {
                        emitter.addInitialize(radius);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Radius is null");
                        }
                    }
                    break;
                case "Mass":
                    var mass: Proton.Mass = this.addMass(next);
                    if (mass != null) {
                        emitter.addInitialize(mass);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Mass is null");
                        }
                    }
                    break;
                case "Life":
                    var life: Proton.Life = this.getLife(next.getAttribute("Life"));
                    if (life != null) {
                        emitter.addInitialize(life);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Life is null");
                        }
                    }
                    break;
                case "Position":
                    var position: Proton.Position = this.addPosition(next);
                    if (position != null) {
                        emitter.addInitialize(position);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Position is null");
                        }
                    }
                    break;
                case "Velocity":
                    var velocity: Proton.Velocity = this.addVelocity(next);
                    if (velocity != null) {
                        emitter.addInitialize(velocity);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Velocity is null");
                        }
                    }
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown initializer");
                    }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Add initializer ImageTarget.
     * This initializer add images instead of shapes as particles.
     * Example:
     *   <ImageTarget Image="IMAGE_1;IMAGE_2;IMAGE_3;IMAGE_4;IMAGE_5;IMAGE_6" />
     * @method addImageTarget
     * @param node is Element from xml
     * @return void
     */
    private addImageTarget(node: Element): Proton.Radius {
        if (node.hasAttribute("Image")) {
            var arr: Array<string> = node.getAttribute("Image").split(";");
            var arrTextures: Array<PIXI.Texture> = [];
            for (var i: number = 0; i < arr.length; i++) {
                var resourceId = arr[i];
                var texture: PIXI.Texture = StageController.getTextureByResourceId(resourceId);
                if (texture != null) {
                    arrTextures.push(texture);
                }
            }
            if (arrTextures != null && arrTextures.length > 0) {
                return new Proton.ImageTarget(arrTextures);
            } else {
                return null;
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Image target has no attribute Image");
            }
        }

        return null;
    }

    /**
     * Add initializer animation as target for particle.
     * Example:
     *   <AnimationTarget Animation="[AnimationId]" Speed="1" Frames="numberOfFrames" Loop="false"/>
     * @method addAnimationTarget
     * @param node is Element from xml
     * @return void
     */
    private addAnimationTarget(node: Element): void {
        if (node.hasAttribute("Animation") && node.hasAttribute("Frames")) {
            if (node.hasAttribute("Speed")) {
                this._animationSpeed = parseFloat(node.getAttribute("Speed"));
            }

            if (node.hasAttribute("Loop")) {
                this._animationLoop = StringUtils.parseBoolean(node.getAttribute("Loop"));
            }

            this._animationName = node.getAttribute("Animation");

            this._textures = [];
            for (var i = 0; i < parseInt(node.getAttribute("Frames")); i++) {
                var texture: PIXI.Texture = PIXI.Texture.fromFrame(
                    node.getAttribute("Animation") + ("0000" + i).slice(-4),
                );
                this._textures.push(texture);
            }

            this._randomSeed = [];
            this._randomSeedLength = this._textures.length * 10;
            for (let i = 0; i < this._randomSeedLength; i++) {
                this._randomSeed.push(Math.floor(Math.random() * this._textures.length));
            }
        }
    }

    /**
     * Add initializer Mass.
     * Example:
     *   <Mass Mass="1;5" />
     * @method addMass
     * @param node is Element from xml
     * @return Proton.Mass
     */
    private addMass(node: Element): Proton.Mass {
        if (node.hasAttribute("Mass")) {
            var arr: Array<number> = ArrayUtils.numberArray(node.getAttribute("Mass"));
            if (arr.length === 3) {
                return new Proton.Mass(arr[0], arr[1], arr[2]);
            } else if (arr.length === 2) {
                return new Proton.Mass(arr[0], arr[1]);
            } else if (arr.length === 1) {
                return new Proton.Mass(arr[0]);
            } else {
                if (DEBUG) {
                    this.log("WARNING Mass attribute doesnt have parameters");
                }
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute Mass in Mass initializer");
            }
        }
        return null;
    }

    /**
     * Add initializer Radius.
     * Example:
     *   <Radius Radius="50;130" />
     * @method addRadius
     * @param node is Element from xml
     * @return Proton.Radius
     */
    private addRadius(node: Element): Proton.Radius {
        if (node.hasAttribute("Radius")) {
            var arr: Array<number> = ArrayUtils.numberArray(node.getAttribute("Radius"));
            if (arr.length === 3) {
                return new Proton.Radius(arr[0], arr[1], arr[2]);
            } else if (arr.length === 2) {
                return new Proton.Radius(arr[0], arr[1]);
            } else if (arr.length === 1) {
                return new Proton.Radius(arr[0]);
            } else {
                if (DEBUG) {
                    this.log("WARNING Radius attribute doesnt have parameters");
                }
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute Radius in Radius initializer");
            }
        }
        return null;
    }

    /**
     * Add initializer Life.
     * Example:
     *   <Life Life="20" />
     * @method getLife
     * @param node is Element from xml
     * @return Proton.Life
     */
    private getLife(attributeValue: string): Proton.Life {
        var arr: Array<number> = ArrayUtils.numberArray(attributeValue);
        if (arr.length === 3) {
            return new Proton.Life(arr[0], arr[1], arr[2]);
        } else if (arr.length === 2) {
            return new Proton.Life(arr[0], arr[1]);
        } else if (arr.length === 1) {
            return new Proton.Life(arr[0]);
        } else {
            if (DEBUG) {
                this.log("WARNING Life attribute doesnt have parameters");
            }
        }
        return null;
    }

    /**
     * Add initializer Span.
     * Example:
     *   <Span Span="1;3;false" />
     * @method getSpan
     * @param node is Element from xml
     * @return Proton.Span
     */
    private getSpan(attributeValue: string): Proton.Span {
        var arr = attributeValue.split(";");
        var from: number = arr.length > 0 ? parseFloat(arr[0]) : undefined;
        var to: number = arr.length > 1 ? parseFloat(arr[1]) : undefined;
        var center: boolean = arr.length > 2 ? !!arr[2] : false;
        if (arr.length) {
            switch (arr.length) {
                case 1:
                    return new Proton.Span(from, from);
                case 2:
                    return new Proton.Span(from, to);
                case 3:
                    return new Proton.Span(from, to, center);
            }
            return null;
        } else {
            if (DEBUG) {
                this.log("WARNING Life attribute doesnt have parameters");
            }
        }
        return null;
    }

    /**
     * Add initializer Position. Position use zones. See method getZone().
     * Example:
     *   <Position>
     *       <RectZone X="0" Y="0" Width="1000" Height="1000" />
     *   </Position>
     * @method addPosition
     * @param node is Element from xml
     * @return Proton.Position
     */
    private addPosition(node: Element): Proton.Position {
        var zone: Element = node.firstElementChild;
        if (zone != null) {
            return new Proton.Position(this.getZone(zone));
        } else {
            if (DEBUG) {
                this.log("WARNING Position has no zone");
            }
        }
        return null;
    }

    /**
     * Add initializer Velocity.
     * Example:
     *   <Velocity RPan="0.1;0.3" ThaPan="0;360" Type="polar" />
     * @method addVelocity
     * @param node is Element from xml
     * @return Proton.Velocity
     */
    private addVelocity(node: Element): Proton.Velocity {
        var rpan: Array<number>;
        var thapan: Array<number>;
        var type: string;

        if (node.getAttribute("RPan")) {
            rpan = ArrayUtils.numberArray(node.getAttribute("RPan"));
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute RPan in Velocity");
            }
        }

        if (node.getAttribute("ThaPan")) {
            thapan = ArrayUtils.numberArray(node.getAttribute("ThaPan"));
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute ThaPan in Velocity");
            }
        }

        if (node.getAttribute("Type")) {
            type = node.getAttribute("Type");
        }

        if (rpan != null && thapan != null) {
            var rpanSpan: any;
            if (rpan.length === 2) {
                rpanSpan = new Proton.Span(rpan[0], rpan[1]);
            } else {
                rpanSpan = rpan[0];
            }

            var thapanSpan: any;
            if (thapan.length === 2) {
                thapanSpan = new Proton.Span(thapan[0], thapan[1]);
            } else {
                thapanSpan = thapan[0];
            }
            if (type != null) {
                return new Proton.Velocity(rpanSpan, thapanSpan, type);
            } else {
                return new Proton.Velocity(rpanSpan, thapanSpan);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING missing some attribute in Velocity");
            }
        }

        return null;
    }

    /**
     * Return zone from xml.
     * @method getZone
     * @param node is Element from xml
     * @return any
     */
    private getZone(node: Element): any {
        switch (node.nodeName) {
            case "RectZone":
                var rectZoneX: number;
                var rectZoneY: number;
                var rectZoneWidth: number;
                var rectZoneHeight: number;

                if (node.hasAttribute("X")) {
                    rectZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in RectZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    rectZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in RectZone");
                    }
                }

                if (node.hasAttribute("Width")) {
                    rectZoneWidth = parseInt(node.getAttribute("Width"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Width in RectZone");
                    }
                }

                if (node.hasAttribute("Height")) {
                    rectZoneHeight = parseInt(node.getAttribute("Height"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Height in RectZone");
                    }
                }

                if (rectZoneX != null && rectZoneY != null && rectZoneWidth != null && rectZoneHeight != null) {
                    return new Proton.RectZone(rectZoneX, rectZoneY, rectZoneWidth, rectZoneHeight);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in RectZone");
                    }
                }
                break;
            case "LineZone":
                var lineZoneX: number;
                var lineZoneY: number;
                var lineZoneWidth: number;
                var lineZoneHeight: number;
                var lineZoneDirection: string;

                if (node.hasAttribute("X")) {
                    lineZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in LineZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    lineZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in LineZone");
                    }
                }

                if (node.hasAttribute("Width")) {
                    lineZoneWidth = parseInt(node.getAttribute("Width"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Width in LineZone");
                    }
                }

                if (node.hasAttribute("Height")) {
                    lineZoneHeight = parseInt(node.getAttribute("Height"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Height in LineZone");
                    }
                }

                if (node.hasAttribute("Direction")) {
                    lineZoneDirection = node.getAttribute("Direction");
                }

                if (lineZoneX != null && lineZoneY != null && lineZoneWidth != null && lineZoneHeight != null) {
                    if (lineZoneDirection != null) {
                        return new Proton.LineZone(
                            lineZoneX,
                            lineZoneY,
                            lineZoneWidth,
                            lineZoneHeight,
                            lineZoneDirection,
                        );
                    } else {
                        return new Proton.LineZone(lineZoneX, lineZoneY, lineZoneWidth, lineZoneHeight);
                    }
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in LineZone");
                    }
                }

                break;
            case "CircleZone":
                var circleZoneX: number;
                var circleZoneY: number;
                var circleZoneRadius: number;

                if (node.hasAttribute("X")) {
                    circleZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in CircleZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    circleZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in CircleZone");
                    }
                }

                if (node.hasAttribute("Radius")) {
                    circleZoneRadius = parseInt(node.getAttribute("Radius"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Radius in CircleZone");
                    }
                }

                if (circleZoneX != null && circleZoneY != null && circleZoneRadius != null) {
                    return new Proton.CircleZone(circleZoneX, circleZoneY, circleZoneRadius);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in CircleZone");
                    }
                }
                break;
            case "PointZone":
                let pointZoneX: number;
                let pointZoneY: number;

                if (node.hasAttribute("X")) {
                    pointZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in PointZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    pointZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in PointZone");
                    }
                }

                if (pointZoneX != null && pointZoneY != null) {
                    return new Proton.PointZone(pointZoneX, pointZoneY);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in PointZone");
                    }
                }

                break;
            case "ImageZone":
                /*let x: number;
                let y: number;

                if (node.hasAttribute('X')) {
                    x = parseInt(node.getAttribute('X'));
                } else {
                    if (DEBUG) {
                        this.log('WARNING no attribute X in PointZone');
                    }
                }

                if (node.hasAttribute('Y')) {
                    y = parseInt(node.getAttribute('Y'));
                } else {
                    if (DEBUG) {
                        this.log('WARNING no attribute Y in PointZone');
                    }
                }*/

                if (node.hasAttribute("Image")) {
                    let texture: PIXI.Texture = StageController.getTextureByResourceId(node.getAttribute("Image"));
                    let particleSprite = new PIXI.Sprite(texture);
                    let pixelData: number[] = StageController.Renderer.extract.pixels(particleSprite);

                    return new Proton.ImageZone(
                        { data: pixelData, width: particleSprite.width, height: particleSprite.height },
                        0,
                        0,
                    );
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in ImageZone");
                    }
                }

                break;
            default:
                if (DEBUG) {
                    this.log("WARNING unknown type zone: " + node.nodeName);
                }
        }
        return null;
    }

    /**
     * Create behaviours from xml for emitter instance.
     * This bevaiours will define behaviour of particle when particle is alive.
     * @method createBehaviours
     * @param node is Element from xml
     * @param emitter Proton.Emitter
     * @return void
     */
    private createBehaviours(node: Element, emitter: Proton.Emitter): void {
        var next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case "AlphaDead":
                    if (next.hasAttribute("Age")) {
                        emitter.addBehaviour(this.alphaDead(parseFloat(next.getAttribute("Age"))));
                    } else {
                        emitter.addBehaviour(this.alphaDead(1));
                    }
                    break;
                case "ScaleDead":
                    emitter.addBehaviour(this.scaleDead());
                    break;
                case "Alpha":
                    var alpha: Proton.Alpha = this.addAlpha(next);
                    if (alpha != null) {
                        emitter.addBehaviour(alpha);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Alpha is null");
                        }
                    }
                    break;
                case "Attraction":
                    var behavior = this.addAttraction(next);
                    if (behavior != null) {
                        emitter.addBehaviour(behavior);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Attraction is null");
                        }
                    }
                    break;
                case "Color":
                    var color: Proton.Color = this.addColor(next);
                    if (color != null) {
                        emitter.addBehaviour(color);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Color is null");
                        }
                    }
                    break;
                case "CrossZone":
                    var crossZone: Proton.CrossZone = this.addCrossZone(next);
                    if (crossZone != null) {
                        emitter.addBehaviour(crossZone);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING CrossZone is null");
                        }
                    }
                    break;
                case "Force":
                    var behavior = this.addForce(next);
                    if (behavior != null) {
                        emitter.addBehaviour(behavior);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Force is null");
                        }
                    }
                    break;
                case "Gravity":
                    var gravity: Proton.Gravity = this.addGravity(next);
                    if (gravity != null) {
                        emitter.addBehaviour(gravity);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Gravity is null");
                        }
                    }
                    break;
                case "GravityWell":
                    var behavior = this.addGravityWell(next);
                    if (behavior != null) {
                        emitter.addBehaviour(behavior);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING GravityWell is null");
                        }
                    }
                    break;
                case "Scale":
                    var scale: Proton.Scale = this.addScale(next);
                    if (scale != null) {
                        emitter.addBehaviour(scale);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Scale is null");
                        }
                    }
                    break;
                case "RandomDrift":
                    var randomDrift: Proton.RandomDrift = this.addRandomDrift(next);
                    if (randomDrift != null) {
                        emitter.addBehaviour(randomDrift);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING RandomDrift is null");
                        }
                    }
                    break;
                case "Rotate":
                    var rotate: Proton.Rotate = this.addRotate(next);
                    if (rotate != null) {
                        emitter.addBehaviour(rotate);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Rotate is null");
                        }
                    }
                    break;
                case "Repulsion":
                    var behavior = this.addRepulsion(next);
                    if (behavior != null) {
                        emitter.addBehaviour(behavior);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Force is null");
                        }
                    }
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown behaviour");
                    }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * AlphaDead is custom custom dead behaviour. After particle reach some age particle will fade out to alpha 0.
     * Example:
     *   <AlphaDead Age="10" />
     * @method alphaDead
     * @param age number
     * @return any
     */
    alphaDead(age: number): any {
        var self: any = this;
        return {
            initialize: function (particle: any) {},
            applyBehaviour: function (particle: any) {
                if (particle.age >= age) {
                    particle["transform"]["alphaA"] = 1;
                    particle["transform"]["alphaB"] = 0;
                }

                if (particle.age >= particle.life) {
                    if (particle.sprite != null) {
                        if (self._textures != null && self._textures.length > 0) {
                            ProtonElement.Pool[self._animationName].push(particle.sprite);
                        } else {
                            ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                        }
                        self.super2.removeChild(particle.sprite);
                    }
                    particle.sprite = null;
                    particle.dead = true;
                    particle = null;
                }
            },
        };
    }

    /**
     * ScaleDead is custom custom dead behaviour. After particle reach some energy particle will scale down to zero.
     * Example:
     *   <ScaleDead />
     * @method scaleDead
     * @return any
     */
    scaleDead(): any {
        var self: any = this;
        return {
            initialize: function (particle: any) {
                particle.scale = 0;
            },
            applyBehaviour: function (particle: any) {
                if (particle.energy >= 2 / 3) {
                    particle.scale = particle.scale * (1 - particle.energy) * 3;
                } else if (particle.energy <= 1 / 3) {
                    particle.scale = particle.scale * (particle.energy * 3);
                }
                if (particle.scale < 0) {
                    if (particle.sprite != null) {
                        if (this._textures != null && this._textures.length > 0) {
                            ProtonElement.Pool[this._animationName].push(particle.sprite);
                        } else {
                            ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                        }
                        self.super2.removeChild(particle.sprite);
                    }
                    particle.sprite = null;
                    particle.dead = true;
                    particle = null;
                }
            },
        };
    }

    /**
     * Add behaviour Alpha.
     * Example:
     *   <Alpha Start="0" End="1" Life="5" />
     * @method addAlpha
     * @param node is Element from xml
     * @return Proton.Alpha
     */
    private addAlpha(node: Element): Proton.Alpha {
        var start: number;
        var end: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("Start")) {
            start = parseFloat(node.getAttribute("Start"));
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = parseFloat(node.getAttribute("End"));
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no attribute End");
            }
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (start != null && end != null) {
            return new Proton.Alpha(start, end, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no Start or End attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour Attraction.
     * Example:
     *   <Attraction X="30" Y="36" Force="10" Radius="150" Life="5" Ease="linear" />
     * @method addAttraction
     * @param node is Element from xml
     * @return Proton.Attraction
     */
    private addAttraction(node: Element): Proton.Attraction {
        var vector: Proton.Vector2D;
        var force: number;
        var radius: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("X") && node.hasAttribute("Y")) {
            vector = new Proton.Vector2D(parseFloat(node.getAttribute("X")), parseFloat(node.getAttribute("Y")));
        } else {
            if (DEBUG) {
                this.log("WARNING Attraction has missing attributes X and/or Y");
            }
            return null;
        }

        if (node.hasAttribute("Force")) {
            force = parseFloat(node.getAttribute("Force"));
        } else {
            if (DEBUG) {
                this.log("WARNING Attraction has missing attribute Force");
            }
        }

        if (node.hasAttribute("Radius")) {
            radius = parseFloat(node.getAttribute("Radius"));
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        return new Proton.Attraction(vector, force, radius, life, ease);
    }

    /**
     * Add behaviour Color.
     * Example:
     *   <Color Color="FFFFFF" EndColor="000000" />
     * @method addColor
     * @param node is Element from xml
     * @return Proton.Color
     */
    private addColor(node: Element): Proton.Color {
        var i: number;
        var arr: Array<string>;
        var endArr: Array<string>;
        var color: Array<string> = [];
        var endColor: Array<string> = [];
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("Color")) {
            arr = node.getAttribute("Color").split(";");
            for (i = 0; i < arr.length; i++) {
                color.push(StringUtils.hexColor(arr[i]));
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Color has no attribute Color");
            }
        }

        if (node.hasAttribute("EndColor")) {
            endArr = node.getAttribute("EndColor").split(";");
            for (i = 0; i < endArr.length; i++) {
                endColor.push(StringUtils.hexColor(endArr[i]));
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Color has no attribute EndColor");
            }
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (color.length > 0) {
            return new Proton.Color(color, endColor, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING Color has no Color attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour CrossZone. CrossZone is used for collisions. Need some zone. See method getZone().
     * Example:
     *   <CrossZone Type="bound">
     *       <LineZone X="0" Y="0" Width="1000" Height="1000" Direction="down" />
     *   </CrossZone>
     * @method addCrossZone
     * @param node is Element from xml
     * @return Proton.CrossZone
     */
    private addCrossZone(node: Element): Proton.CrossZone {
        var zone: any;
        var type: string;
        var life: Proton.Life;
        var ease: string;

        if (node.firstElementChild != null) {
            zone = this.getZone(node.firstElementChild);
        } else {
            if (DEBUG) {
                this.log("WARNING no zone in CrossZone");
            }
        }

        if (node.hasAttribute("Type")) {
            type = node.getAttribute("Type");
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (zone != null) {
            return new Proton.CrossZone(zone, type, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING no zone in CrossZone");
            }
        }

        return null;
    }

    /**
     * Add behaviour Force (force vector)
     * Example:
     *   <Force X="30" Y="36" Life="5" Ease="linear" />
     * @method addFOrce
     * @param node is Element from xml
     * @return Proton.Force
     */
    private addForce(node: Element): Proton.Attraction {
        var x: number;
        var y: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (node.hasAttribute("X") && node.hasAttribute("Y")) {
            x = parseFloat(node.getAttribute("X"));
            y = parseFloat(node.getAttribute("Y"));
        } else {
            if (DEBUG) {
                this.log("WARNING Attraction has missing attributes X and/or Y");
            }
            return null;
        }

        return new Proton.Force(x, y, life, ease);
    }

    /**
     * Add behaviour Gravity.
     * Example:
     *   <Gravity Gravity="5" />
     * @method addGravity
     * @param node is Element from xml
     * @return Proton.Gravity
     */
    private addGravity(node: Element): Proton.Gravity {
        var gravity: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("Gravity")) {
            gravity = parseFloat(node.getAttribute("Gravity"));
        } else {
            if (DEBUG) {
                this.log("WARNING Gravity has no attribute Gravity");
            }
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (gravity != null) {
            return new Proton.Gravity(gravity, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING Gravity has no valid attributes");
            }
        }

        return null;
    }

    /**
     * Add behaviour GravityWell.
     * Example:
     *   <GravityWell X="30" Y="36" Force="10" Life="5" Ease="linear" />
     * @method addGravityWell
     * @param node is Element from xml
     * @return Proton.GravityWell
     */
    private addGravityWell(node: Element): Proton.GravityWell {
        var vector: Proton.Vector2D;
        var force: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("X") && node.hasAttribute("Y")) {
            vector = new Proton.Vector2D(parseFloat(node.getAttribute("X")), parseFloat(node.getAttribute("Y")));
        } else {
            if (DEBUG) {
                this.log("WARNING GravityWell has missing attributes X and/or Y");
            }
            return null;
        }

        if (node.hasAttribute("Force")) {
            force = parseFloat(node.getAttribute("Force"));
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        return new Proton.GravityWell(vector, force, life, ease);
    }

    /**
     * Add behaviour Scale.
     * Example:
     *   <Scale Start="1" End="1.5" />
     * @method addScale
     * @param node is Element from xml
     * @return Proton.Scale
     */
    private addScale(node: Element): Proton.Scale {
        var start: Proton.Span;
        var end: Proton.Span;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("Start")) {
            start = this.getSpan(node.getAttribute("Start"));
        } else {
            if (DEBUG) {
                this.log("WARNING Scale has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = this.getSpan(node.getAttribute("End"));
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (start != null) {
            return new Proton.Scale(start, end, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING Scale has no Start or End attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour RandomDrift.
     * Example:
     *   <RandomDrift DriftX="5" DriftY="0" Delay="0.15"/>
     * @method addRandomDrift
     * @param node is Element from xml
     * @return Proton.RandomDrift
     */
    private addRandomDrift(node: Element): Proton.RandomDrift {
        var driftX: number;
        var driftY: number;
        var delay: number = 0;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("DriftX")) {
            driftX = parseFloat(node.getAttribute("DriftX"));
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no attribute DriftX");
            }
        }

        if (node.hasAttribute("DriftY")) {
            driftY = parseFloat(node.getAttribute("DriftY"));
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no attribute DriftY");
            }
        }

        if (node.hasAttribute("Delay")) {
            delay = parseFloat(node.getAttribute("Delay"));
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        if (driftX != null && driftY != null && delay != null) {
            return new Proton.RandomDrift(driftX, driftY, delay, life, ease);
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no valid attributes");
            }
        }

        return null;
    }

    /**
     * Add behaviour Repulsion.
     * Example:
     *   <Repulsion X="30" Y="36" Force="10" Radius="150" Life="5" Ease="linear" />
     * @method addRepulsion
     * @param node is Element from xml
     * @return Proton.Repulsion
     */
    private addRepulsion(node: Element): Proton.Repulsion {
        var vector: Proton.Vector2D;
        var force: number;
        var radius: number;
        var life: Proton.Life;
        var ease: string;

        if (node.hasAttribute("X") && node.hasAttribute("Y")) {
            vector = new Proton.Vector2D(parseFloat(node.getAttribute("X")), parseFloat(node.getAttribute("Y")));
        } else {
            if (DEBUG) {
                this.log("WARNING Repulsion has missing attributes X and/or Y");
            }
            return null;
        }

        if (node.hasAttribute("Force")) {
            force = parseFloat(node.getAttribute("Force"));
        } else {
            if (DEBUG) {
                this.log("WARNING Repulsion has missing attribute Force");
            }
        }

        if (node.hasAttribute("Radius")) {
            radius = parseFloat(node.getAttribute("Radius"));
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        return new Proton.Repulsion(vector, force, radius, life, ease);
    }

    /**
     * Add behaviour Rotate.
     * Example:
     *   <Rotate Start="0;360" End="-0.5;0.5" Style="add" />
     * @method addRotate
     * @param node is Element from xml
     * @return Proton.Rotate
     */
    private addRotate(node: Element): Proton.Rotate {
        var start: Proton.Span | string;
        var end: Proton.Span;
        var style: string;
        var life: Proton.Life;
        var ease: string;

        // This can be "Velocity"/"velocity"/"v" to align particle along the direction of travel
        if (node.hasAttribute("Start")) {
            if (isNaN(parseInt(node.getAttribute("Start")))) {
                start = node.getAttribute("Start");
            } else {
                start = this.getSpan(node.getAttribute("Start"));
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Scale has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = this.getSpan(node.getAttribute("End"));
        }

        if (node.hasAttribute("Style")) {
            style = node.getAttribute("Style");
        }

        if (node.hasAttribute("Life")) {
            life = this.getLife(node.getAttribute("Life"));
        }

        if (node.hasAttribute("Ease")) {
            ease = node.getAttribute("Ease");
        }

        return new Proton.Rotate(start, end, style, life, ease);
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    clone(): ProtonElement {
        var element: ProtonElement = new ProtonElement();

        element.super2.position.x = this.super2.position.x;
        element.super2.position.y = this.super2.position.y;
        element.super2.pivot.x = this.super2.pivot.x;
        element.super2.pivot.y = this.super2.pivot.y;
        element.super2.scale.x = this.super2.scale.x;
        element.super2.scale.y = this.super2.scale.y;
        element.super2.visible = this.super2.visible;
        element._blendMode = this._blendMode;
        element._tint = this._tint;

        // if(this._emitterBaseElement) {
        //     element._emitterBaseElement = this._emitterBaseElement;
        // }

        var node: Element = document.createElement("particle");
        node.setAttribute("Style2", this._styleName);

        element.create(node);
        element.super2._markAsCloned(); // must be called last before return!
        return element;
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    private static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(ProtonElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this.super2, "alpha");

        let alphaTween: Array<number>;

        let alphaTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ALPHA_TWEEN_POSITION,
            null,
        );

        if (alphaTweenPosition == ProtonElementAttributes.ALPHA_TWEEN_POSITION_VALUE.RELATIVE) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ALPHA_TWEEN)).map(
                (value) => value + this.super2.alpha,
            );
        } else if (alphaTweenPosition == ProtonElementAttributes.ALPHA_TWEEN_POSITION_VALUE.SMOOTH) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ALPHA_TWEEN));
            if (alphaTween.length) {
                alphaTween[0] = this.super2.alpha;
            }
        } else {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ALPHA_TWEEN));
        }

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ALPHA_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.ALPHA_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.ALPHA_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.ALPHA_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ALPHA_TWEEN_KEY_TIMES))
            : null;

        Tweens.alphaTween(this.super2, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this.super2, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition === ProtonElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.super2.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.super2.position.y,
            );
        } else if (positionTweenPosition == ProtonElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_Y));
            if (positionTweenX.length) {
                positionTweenX[0] = this.super2.position.x;
            }
            if (positionTweenY.length) {
                positionTweenY[0] = this.super2.position.y;
            }
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_Y));
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_BEZIER_POINTS));
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this.super2, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this.super2, "rotation");

        let rotationTween: Array<number>;

        let rotationTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_POSITION,
            null,
        );
        if (rotationTweenPosition == ProtonElementAttributes.ROTATION_TWEEN_POSITION_VALUE.RELATIVE) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN)).map(
                (value) => value + Tweens.radians2degrees(this.super2.rotation),
            );
        } else if (rotationTweenPosition == ProtonElementAttributes.ROTATION_TWEEN_POSITION_VALUE.SMOOTH) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN));
            if (rotationTween.length) {
                rotationTween[0] = Tweens.radians2degrees(this.super2.rotation);
            }
        } else {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN));
        }

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.ROTATION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN_KEY_TIMES))
            : null;

        Tweens.rotationTween(this.super2, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        if (
            !node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_X) &&
            !node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_Y) &&
            !node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN)
        ) {
            return false;
        }

        Tweens.removeTweens(this.super2, "scale");

        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN)) {
            scaleTween = ProtonElement._getPointsFromPointsAttribute(
                node.getAttribute(ProtonElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        let scaleTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.SCALE_TWEEN_POSITION,
            null,
        );

        if (scaleTween && scaleTween.length) {
            if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTween.map((value: PIXI.Point) => {
                    return {
                        x: value.x + this.super2.scale.x,
                        y: value.y + this.super2.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTween;
                if (tweenValues.length) {
                    tweenValues[0].x = this.super2.scale.x;
                    tweenValues[0].y = this.super2.scale.y;
                }
            } else {
                tweenValues = scaleTween;
            }
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue + this.super2.scale.x,
                            y: scaleTweenY[i] + this.super2.scale.y,
                        } as PIXI.Point;
                    });
                } else if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                    if (tweenValues.length) {
                        tweenValues[0].x = this.super2.scale.x;
                        tweenValues[0].y = this.super2.scale.y;
                    }
                } else {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                }
            }
        } else if (scaleTweenX) {
            if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue + this.super2.scale.x,
                        y: this.super2.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.super2.scale.y,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].x = this.super2.scale.x;
                }
            } else {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.super2.scale.y,
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenY) {
            if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.super2.scale.x,
                        y: scaleYValue + this.super2.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ProtonElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.super2.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].y = this.super2.scale.y;
                }
            } else {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.super2.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
            }
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.SCALE_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.SCALE_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.SCALE_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.SCALE_TWEEN_KEY_TIMES))
            : null;

        Tweens.scaleTween(this.super2, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the position tween for emitter
     * @method runPositionTweenEmitter
     * @param node
     * @returns {boolean}
     */
    public runPositionTweenEmitter(node: Element): boolean {
        if (!this._emitterBaseElement) {
            return false;
        }

        if (
            !node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_X) ||
            !node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_Y)
        ) {
            this._emitterBaseElement._activeUpdateEmitterPosition = false;
            return false;
        }

        this._emitterBaseElement._activeUpdateEmitterPosition = true;

        Tweens.removeTweens(this._emitterBaseElement, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_EMITTER_POSITION,
            null,
        );
        if (positionTweenPosition === ProtonElementAttributes.POSITION_TWEEN_EMITTER_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_X),
            ).map((value) => value + this._emitterBaseElement.position.x);
            positionTweenY = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_Y),
            ).map((value) => value + this._emitterBaseElement.position.y);
        } else if (positionTweenPosition == ProtonElementAttributes.POSITION_TWEEN_EMITTER_POSITION_VALUE.SMOOTH) {
            positionTweenX = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_X),
            );
            positionTweenY = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_Y),
            );
            if (positionTweenX.length) {
                positionTweenX[0] = this._emitterBaseElement.position.x;
            }
            if (positionTweenY.length) {
                positionTweenY[0] = this._emitterBaseElement.position.y;
            }
        } else {
            positionTweenX = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_X),
            );
            positionTweenY = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_Y),
            );
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_EMITTER_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_EMITTER_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.POSITION_TWEEN_EMITTER_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_EMITTER_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_EMITTER_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(
                node.getAttribute(ProtonElementAttributes.POSITION_TWEEN_EMITTER_BEZIER_POINTS),
            );
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                ProtonElementAttributes.POSITION_TWEEN_EMITTER_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this._emitterBaseElement, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });
    }

    /**
     * Runs the rotation tween for emitter
     * @method runScaleTweenEmitter
     * @param node
     * @returns {boolean}
     */
    public runRotationTweenEmitter(node: Element): boolean {
        if (!this._emitterBaseElement) {
            return false;
        }

        if (
            !node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER) ||
            !node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER_RADIUS)
        ) {
            this._emitterBaseElement._activeUpdateEmitterRotation = false;
            return false;
        }

        this._emitterBaseElement._activeUpdateEmitterRotation = true;
        this._emitterBaseElement._rotationRadius = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EMITTER_RADIUS,
            0,
        );

        Tweens.removeTweens(this._emitterBaseElement, "rotation");

        let rotationTween: Array<number>;

        let rotationTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EMITTER_POSITION,
            null,
        );
        if (rotationTweenPosition == ProtonElementAttributes.ROTATION_TWEEN_EMITTER_POSITION_VALUE.RELATIVE) {
            rotationTween = ArrayUtils.numberArray(
                node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER),
            ).map((value) => value + Tweens.radians2degrees(this._emitterBaseElement.rotation));
        } else if (rotationTweenPosition == ProtonElementAttributes.ROTATION_TWEEN_EMITTER_POSITION_VALUE.SMOOTH) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER));
            if (rotationTween.length) {
                rotationTween[0] = Tweens.radians2degrees(this._emitterBaseElement.rotation);
            }
        } else {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER));
        }

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EMITTER_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EMITTER_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ProtonElementAttributes.ROTATION_TWEEN_EMITTER_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ProtonElementAttributes.ROTATION_TWEEN_EMITTER_KEY_TIMES))
            : null;

        Tweens.rotationTween(this._emitterBaseElement, {
            degrees,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });
    }

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */
    private formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.super2.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */
    private formatPoints(points: string, useGlobal: boolean = false): Array<Object> {
        let result: Array<Object> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(",");

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                //let point: PIXI.Point = new PIXI.Point(x, y);
                let objectPoint: Object = { x: x, y: y };
                result.push(objectPoint);
            }
        }

        return result;
    }
}

@engineComponent(ComponentTypes.DisplayObject)
export class EmitterBaseElement extends BaseElement {
    public _protonEmitters: Array<Proton.Emitter>;

    public _rotationRadius: number = -1;

    public _activeUpdateEmitterPosition: boolean = false;
    public _activeUpdateEmitterRotation: boolean = false;

    public updateEmitterParameters(): void {
        this.updateEmitterPosition();
        this.updateEmitterRotation();
    }

    public stopUpdateEmitterParameters(): void {
        this._activeUpdateEmitterPosition = false;
        this._activeUpdateEmitterRotation = false;

        if (this["position"]) {
            Tweens.removeTweens(this, "position");
        }

        if (this["rotation"]) {
            Tweens.removeTweens(this, "rotation");
        }
    }

    private updateEmitterPosition(): void {
        if (this._activeUpdateEmitterPosition && this._protonEmitters != null) {
            if (this["position"] != null && this["position"].x != null && this["position"].y != null) {
                for (var i = 0; i < this._protonEmitters.length; i++) {
                    this._protonEmitters[i].p.x = this["position"].x;
                    this._protonEmitters[i].p.y = this["position"].y;
                }
            }
        }
    }

    private updateEmitterRotation(): void {
        if (this._activeUpdateEmitterRotation && this._protonEmitters && this._rotationRadius >= 0) {
            if (this["rotation"] != null) {
                for (var i = 0; i < this._protonEmitters.length; i++) {
                    this._protonEmitters[i].p.x = this.position.x + Math.cos(this["rotation"]) * this._rotationRadius;
                    this._protonEmitters[i].p.y = this.position.y + Math.sin(this["rotation"]) * this._rotationRadius;
                }
            }
        }
    }
}

export const ProtonElementAttributes = {
    EMIT: "Emit",
    START: "Start",
    STOP: "Stop",
    STOPPING: "Stopping",
    DURATION: "Duration",

    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    EMITTER_X: "EmitterX",
    EMITTER_Y: "EmitterY",

    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /*
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",

    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ALPHA_TWEEN_KEY_TIMES: "AlphaTweenKeyTimes",
    ALPHA_TWEEN_POSITION: "AlphaTweenPosition",
    ALPHA_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    POSITION_TWEEN_KEY_TIMES: "PositionTweenKeyTimes",
    /**
     * Bezier's "type" of curve
     * VALUES:
     *      thru
     *      soft
     *      quadratic
     *      cubic
     * EXAMPLE:
     *      PositionTweenBezierType="thru"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540" '
     * @property PositionTweenBezierType
     * @default thru
     */
    POSITION_TWEEN_BEZIER_TYPE: "PositionTweenBezierType",
    /**
     * the tension on the Bezier
     * VALUES:
     *      0 has no curviness (straight lines),
     *      1 is normal curviness,
     *      2 is twice the normal curviness,
     *      etc. Use any number, not just integers
     * EXAMPLE:
     *      PositionTweenBezierCurviness="1.25"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540" '
     * @property PositionTweenBezierCurviness
     * @default 1.0
     */
    POSITION_TWEEN_BEZIER_CURVINESS: "PositionTweenBezierCurviness",
    /**
     * Array points, pairs of X,Y coords for movement along the bezier curve
     * EXAMPLE:
     *                                   x   y     x  y    x   y
     *      PositionTweenBezierPoints="[100,250];[300,0];[500,400]"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540" '
     * @property PositionTweenBezierPoints
     * @default null
     */
    POSITION_TWEEN_BEZIER_POINTS: "PositionTweenBezierPoints",
    /**
     * Auto rotation object on Bezier
     * VALUES:
     *      true
     *      false
     * EXAMPLE:
     *      PositionTweenBezierAutoRotate="true"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierAutoRotate
     * @default false
     */
    POSITION_TWEEN_BEZIER_AUTOROTATE: "PositionTweenBezierAutoRotate",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ROTATION_TWEEN_KEY_TIMES: "RotationTweenKeyTimes",
    ROTATION_TWEEN_POSITION: "RotationTweenPosition",
    ROTATION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /**
     *
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    SCALE_TWEEN_KEY_TIMES: "ScaleTweenKeyTimes",
    SCALE_TWEEN_POSITION: "ScaleTweenPosition",
    SCALE_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",

    /* **********************************************************
     *
     *  Attributes for EMITTER
     *
     * ***********************************************************/
    POSITION_TWEEN_EMITTER_X: "PositionTweenEmitterX",
    POSITION_TWEEN_EMITTER_Y: "PositionTweenEmitterY",
    POSITION_TWEEN_EMITTER_EASE: "PositionTweenEmitterEase",
    POSITION_TWEEN_EMITTER_DURATION: "PositionTweenEmitterDuration",
    POSITION_TWEEN_EMITTER_DELAY: "PositionTweenEmitterDelay",
    POSITION_TWEEN_EMITTER_POSITION: "PositionTweenEmitterPosition",
    POSITION_TWEEN_EMITTER_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    POSITION_TWEEN_EMITTER_LOOP: "PositionTweenEmitterLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    POSITION_TWEEN_EMITTER_KEY_TIMES: "PositionTweenEmitterKeyTimes",
    /**
     * Bezier's "type" of curve
     * VALUES:
     *      thru
     *      soft
     *      quadratic
     *      cubic
     * EXAMPLE:
     *      PositionTweenEmitterBezierType="thru"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenEmitterX="960;960"  PositionTweenEmitterY="540;540" '
     * @property PositionTweenEmitterBezierType
     * @default thru
     */
    POSITION_TWEEN_EMITTER_BEZIER_TYPE: "PositionTweenEmitterBezierType",
    /**
     * the tension on the Bezier
     * VALUES:
     *      0 has no curviness (straight lines),
     *      1 is normal curviness,
     *      2 is twice the normal curviness,
     *      etc. Use any number, not just integers
     * EXAMPLE:
     *      PositionTweenEmitterBezierCurviness="1.25"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenEmitterX="960;960"  PositionTweenEmitterY="540;540" '
     * @property PositionTweenEmitterBezierCurviness
     * @default 1.0
     */
    POSITION_TWEEN_EMITTER_BEZIER_CURVINESS: "PositionTweenEmitterBezierCurviness",
    /**
     * Array points, pairs of X,Y coords for movement along the bezier curve
     * EXAMPLE:
     *                                   x   y     x  y    x   y
     *      PositionTweenEmitterBezierPoints="[100,250];[300,0];[500,400]"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenEmitterX="960;960"  PositionTweenEmitterY="540;540" '
     * @property PositionTweenEmitterBezierPoints
     * @default null
     */
    POSITION_TWEEN_EMITTER_BEZIER_POINTS: "PositionTweenEmitterBezierPoints",
    /**
     * Auto rotation object on Bezier
     * VALUES:
     *      true
     *      false
     * EXAMPLE:
     *      PositionTweenEmitterBezierAutoRotate="true"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenEmitterBezierAutoRotate
     * @default false
     */
    POSITION_TWEEN_EMITTER_BEZIER_AUTOROTATE: "PositionTweenEmitterBezierAutoRotate",

    RESET_BEHAVIORS: "ResetBehaviors",

    ROTATION_TWEEN_EMITTER: "RotationTweenEmitter",
    ROTATION_TWEEN_EMITTER_RADIUS: "RotationTweenEmitterRadius",
    ROTATION_TWEEN_EMITTER_EASE: "RotationTweenEmitterEase",
    ROTATION_TWEEN_EMITTER_DURATION: "RotationTweenEmitterDuration",
    ROTATION_TWEEN_EMITTER_DELAY: "RotationTweenEmitterDelay",
    ROTATION_TWEEN_EMITTER_LOOP: "RotationTweenEmitterLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ROTATION_TWEEN_EMITTER_KEY_TIMES: "RotationTweenEmitterKeyTimes",
    ROTATION_TWEEN_EMITTER_POSITION: "RotationTweenEmitterPosition",
    ROTATION_TWEEN_EMITTER_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    VISIBLE: "Visible",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ProtonElement.ts
 **/